import { Component, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgIf } from '@angular/common';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TxpIntegersOnlyDirective } from '../../../directives/integers-only/1.0/txp-integers-only.directive';

@Component({
  selector: 'txp-item-threshold-template-form',
  templateUrl: './item-threshold-template-form.component.html',
  styleUrls: ['./item-threshold-template-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ItemThresholdTemplateFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    TxpIntegersOnlyDirective,
    MatError,
    TranslateModule,
  ],
})
export class ItemThresholdTemplateFormComponent implements ControlValueAccessor {
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize;
  form: UntypedFormGroup;
  onTouched: any;
  disabled: boolean;
  onChangeFn: any;
  defaultFormValue: any;

  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {
    this.form = this.formBuilder.group({
      itemThresholdAdditionalTime: new UntypedFormControl(''),
      itemThresholdMaxItems: new UntypedFormControl('', [Validators.min(1)]),
    });
    this.defaultFormValue = this.form.value;
  }

  writeValue(obj): void {
    const newValue = {
      itemThresholdAdditionalTime: obj.itemThresholdAdditionalTime || null,
      itemThresholdMaxItems: obj.itemThresholdMaxItems || null,
    };

    this.form.reset(newValue, { emitEvent: false });
    this.handleChange(newValue);
  }

  handleChange(data) {
    if (this.onChangeFn) {
      this.onChangeFn({
        itemThresholdAdditionalTime: data.itemThresholdAdditionalTime || null,
        itemThresholdMaxItems: data.itemThresholdMaxItems || null,
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      fn({
        itemThresholdAdditionalTime: data.itemThresholdAdditionalTime || null,
        itemThresholdMaxItems: data.itemThresholdMaxItems || null,
      });
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
